import { useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Collapse,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  Link,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from "@material-ui/core";
import LoadingButton from "@material-ui/lab/LoadingButton";
import SearchIcon from "@material-ui/icons/Search";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";
import EditOutlinedIcon from "@material-ui/icons/EditOutlined";
import { validateRut, formatRut, RutFormat } from "@fdograph/rut-utilities";

import { skipToken } from "@reduxjs/toolkit/query";
import { clientesApi } from "src/services/clientesApi";
import { dteApi } from "src/services/dteApi";

export default function ReceptorSection({ tarea, formik }) {
  const { values, errors, touched, setFieldValue, handleChange, handleBlur } =
    formik;

  const { data: clientes = [], isLoading: cargandoClientes } =
    clientesApi.useGetClientesQuery();

  const [obtenerInfoContribuyente, { isFetching: cargandoSII }] =
    dteApi.useLazyObtenerInfoContribuyenteQuery();

  const [clienteSeleccionado, setClienteSeleccionado] = useState(null);
  const [mostrarBuscarRut, setMostrarBuscarRut] = useState(false);
  const [rutIngresado, setRutIngresado] = useState("");
  const [errorRut, setErrorRut] = useState(null);
  const [errorSII, setErrorSII] = useState(null);
  const [edicionManual, setEdicionManual] = useState(false);

  const esRutProvisorioClienteOriginal = tarea?.cliente?.esRutProvisorio === 1;

  const receptorCliente =
    values.tipoFacturacion === "MismaEmpresa"
      ? (tarea?.cliente ?? null)
      : clienteSeleccionado;

  // El objeto receptor puede venir con shape parcial (sin correoFacturacion).
  // Consultamos el cliente vivo por rut para evaluar el dato real.
  const { data: receptorClienteLive } = clientesApi.useGetClienteQuery(
    receptorCliente?.rut ? { rut: receptorCliente.rut } : skipToken,
  );

  const correoFacturacionFalta =
    receptorClienteLive != null &&
    (!receptorClienteLive.correoFacturacion ||
      receptorClienteLive.correoFacturacion.trim() === "");

  const limpiarReceptor = () => {
    setFieldValue("rutReceptor", "");
    setFieldValue("razonSocialReceptor", "");
    setFieldValue("direccionReceptor", "");
    setFieldValue("comunaReceptor", "");
    setFieldValue("giroReceptor", "");
    setClienteSeleccionado(null);
    setMostrarBuscarRut(false);
    setRutIngresado("");
    setErrorRut(null);
    setErrorSII(null);
    setEdicionManual(false);
  };

  const handleChangeTipoFacturacion = (event) => {
    const nuevoTipo = event.target.value;
    setFieldValue("tipoFacturacion", nuevoTipo);
    if (nuevoTipo === "MismaEmpresa") limpiarReceptor();
  };

  const aplicarDatosSII = (datosSII, rut, fallback = {}) => {
    const razonSocial = datosSII?.RznSocRecep || fallback.nombre || "";
    const direccion = datosSII?.DirRecep || fallback.direccion || "";
    const comuna = datosSII?.CmnaRecep || fallback.comuna || "";
    const giro = datosSII?.GiroRecep || "";
    setFieldValue("rutReceptor", rut);
    setFieldValue("razonSocialReceptor", razonSocial);
    setFieldValue("direccionReceptor", direccion);
    setFieldValue("comunaReceptor", comuna);
    setFieldValue("giroReceptor", giro);
    setEdicionManual(!razonSocial || !direccion || !comuna);
  };

  const handleSeleccionarCliente = async (cliente) => {
    setClienteSeleccionado(cliente);
    if (!cliente) {
      limpiarReceptor();
      return;
    }
    setErrorSII(null);
    try {
      const datosSII = await obtenerInfoContribuyente(cliente.rut).unwrap();
      aplicarDatosSII(datosSII, cliente.rut, cliente);
    } catch {
      setErrorSII(
        "No pudimos consultar SII. Revisa los datos del receptor e ingresa lo que falte manualmente.",
      );
      setFieldValue("rutReceptor", cliente.rut);
      setFieldValue("razonSocialReceptor", cliente.nombre || "");
      setFieldValue("direccionReceptor", cliente.direccion || "");
      setFieldValue("comunaReceptor", cliente.comuna || "");
      setFieldValue("giroReceptor", "");
      setEdicionManual(true);
    }
  };

  const handleBuscarPorRut = async () => {
    setErrorRut(null);
    setErrorSII(null);
    const rut = rutIngresado.toLowerCase().trim();
    if (!validateRut(rut)) {
      setErrorRut("RUT inválido");
      return;
    }
    try {
      const datosSII = await obtenerInfoContribuyente(rut).unwrap();
      aplicarDatosSII(datosSII, rut);
      setMostrarBuscarRut(false);
      setClienteSeleccionado(null);
    } catch {
      setErrorSII(
        "No pudimos consultar SII. Puedes ingresar los datos manualmente.",
      );
      setFieldValue("rutReceptor", rut);
      setFieldValue("razonSocialReceptor", "");
      setFieldValue("direccionReceptor", "");
      setFieldValue("comunaReceptor", "");
      setFieldValue("giroReceptor", "");
      setEdicionManual(true);
      setMostrarBuscarRut(false);
      setClienteSeleccionado(null);
    }
  };

  return (
    <Card variant="outlined">
      <CardContent>
        <Typography variant="h6" sx={{ mb: 2 }}>
          📍 Receptor
        </Typography>

        {esRutProvisorioClienteOriginal &&
          values.tipoFacturacion === "MismaEmpresa" && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              El cliente original tiene <strong>RUT provisorio</strong>. La
              emisión será rechazada por el backend. Cambia a{" "}
              <em>Facturar a otra empresa</em> o actualiza primero el RUT del
              cliente.
            </Alert>
          )}

        <FormControl component="fieldset">
          <FormLabel component="legend">Empresa a facturar</FormLabel>
          <RadioGroup
            row
            name="tipoFacturacion"
            value={values.tipoFacturacion}
            onChange={handleChangeTipoFacturacion}
          >
            <FormControlLabel
              value="MismaEmpresa"
              control={<Radio />}
              label="Facturar a la misma empresa (cliente original)"
            />
            <FormControlLabel
              value="OtraEmpresa"
              control={<Radio />}
              label="Facturar a otra empresa"
            />
          </RadioGroup>
        </FormControl>

        {values.tipoFacturacion === "MismaEmpresa" &&
          esRutProvisorioClienteOriginal && (
            <Box sx={{ mt: 1 }}>
              <Chip label="RUT provisorio" color="warning" size="small" />
            </Box>
          )}

        {correoFacturacionFalta && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            Este cliente no tiene correo de facturación. La factura se podrá
            emitir, pero no se enviará por correo hasta completarlo.{" "}
            <Link
              href={`/dashboard/clientes/${receptorClienteLive.rut}/completar-datos-formales`}
              target="_blank"
              rel="noopener noreferrer"
              underline="always"
            >
              Completar datos formales
            </Link>
          </Alert>
        )}

        {values.tipoFacturacion === "OtraEmpresa" && (
          <Box sx={{ mt: 2 }}>
            <Autocomplete
              fullWidth
              options={clientes}
              value={clienteSeleccionado}
              getOptionLabel={(option) =>
                option?.nombre
                  ? `${option.nombre} (${option.rut})`
                  : option?.rut || ""
              }
              isOptionEqualToValue={(opt, val) => opt?.rut === val?.rut}
              loading={cargandoClientes}
              disabled={cargandoSII}
              onChange={(_, newValue) => handleSeleccionarCliente(newValue)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Seleccionar cliente existente"
                  placeholder="Busca por nombre o RUT..."
                />
              )}
            />

            {cargandoSII && (
              <Stack
                direction="row"
                alignItems="center"
                spacing={1}
                sx={{ mt: 1 }}
              >
                <CircularProgress size={14} />
                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                  Consultando datos en el SII...
                </Typography>
              </Stack>
            )}

            <Box sx={{ mt: 1 }}>
              <Button
                size="small"
                startIcon={<SearchIcon />}
                disabled={cargandoSII}
                onClick={() => setMostrarBuscarRut((v) => !v)}
              >
                {mostrarBuscarRut
                  ? "Ocultar búsqueda por RUT"
                  : "¿No aparece? Buscar por RUT en SII"}
              </Button>
            </Box>

            <Collapse in={mostrarBuscarRut}>
              <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                <TextField
                  label="RUT"
                  value={rutIngresado}
                  onChange={(e) => {
                    setRutIngresado(e.target.value);
                    setErrorRut(null);
                  }}
                  error={!!errorRut}
                  helperText={errorRut}
                  fullWidth
                />
                <LoadingButton
                  variant="contained"
                  onClick={handleBuscarPorRut}
                  loading={cargandoSII}
                >
                  Buscar
                </LoadingButton>
              </Stack>
            </Collapse>

            {errorSII && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                {errorSII}
              </Alert>
            )}

            {values.rutReceptor && (
              <Box
                sx={{
                  mt: 2,
                  p: 2,
                  borderRadius: 1,
                  border: "1px solid",
                  borderColor: edicionManual ? "warning.light" : "divider",
                  bgcolor: edicionManual
                    ? "transparent"
                    : (theme) => theme.palette.action.hover,
                }}
              >
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={1}
                  sx={{ mb: 1.5 }}
                >
                  {edicionManual ? (
                    <EditOutlinedIcon
                      sx={{ color: "warning.main", fontSize: 18 }}
                    />
                  ) : (
                    <CheckCircleOutlineIcon
                      sx={{ color: "success.main", fontSize: 18 }}
                    />
                  )}
                  <Typography
                    variant="caption"
                    sx={{
                      color: "text.secondary",
                      fontWeight: 600,
                      textTransform: "uppercase",
                      letterSpacing: 0.5,
                    }}
                  >
                    {edicionManual
                      ? "Completar datos del receptor manualmente"
                      : "Datos verificados en SII"}
                  </Typography>
                </Stack>

                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <TextField
                      label="RUT"
                      value={formatRut(values.rutReceptor, RutFormat.DOTS_DASH)}
                      disabled
                      fullWidth
                      size="small"
                      variant="filled"
                    />
                  </Grid>
                  <Grid item xs={12} md={8}>
                    <TextField
                      label="Razón social"
                      name="razonSocialReceptor"
                      value={values.razonSocialReceptor}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={
                        touched.razonSocialReceptor &&
                        !!errors.razonSocialReceptor
                      }
                      helperText={
                        touched.razonSocialReceptor &&
                        errors.razonSocialReceptor
                      }
                      disabled={!edicionManual}
                      fullWidth
                      size="small"
                      variant={edicionManual ? "outlined" : "filled"}
                    />
                  </Grid>
                  <Grid item xs={12} md={8}>
                    <TextField
                      label="Dirección"
                      name="direccionReceptor"
                      value={values.direccionReceptor}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={
                        touched.direccionReceptor && !!errors.direccionReceptor
                      }
                      helperText={
                        touched.direccionReceptor && errors.direccionReceptor
                      }
                      disabled={!edicionManual}
                      fullWidth
                      size="small"
                      variant={edicionManual ? "outlined" : "filled"}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      label="Comuna"
                      name="comunaReceptor"
                      value={values.comunaReceptor}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.comunaReceptor && !!errors.comunaReceptor}
                      helperText={
                        touched.comunaReceptor && errors.comunaReceptor
                      }
                      disabled={!edicionManual}
                      fullWidth
                      size="small"
                      variant={edicionManual ? "outlined" : "filled"}
                    />
                  </Grid>
                </Grid>
              </Box>
            )}
          </Box>
        )}
      </CardContent>
    </Card>
  );
}
